import { FC, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Breadcrumbs } from '@dbt-labs/sourdough';

import { WarehouseEntityType } from '@/__generated__/graphql';
import { isWarehouseAssetId } from '@/context/AccountNavigation/utils';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useWarehouseEntity } from '@/hooks/discovery/catalog/entities';
import { useLinkToExplorer } from '@/hooks/links';
import { useValidatedParams } from '@/hooks/params';

export const INCLUDES_CATEGORY = [
  WarehouseEntityType.Schema,
  WarehouseEntityType.Table,
  WarehouseEntityType.View,
];

export const AssetBreadcrumbs: FC = () => {
  const { accountId } = useDbtCloudAccount();
  const { assetId: urn } = useValidatedParams();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'tables';

  const getMultiProjectLink = useLinkToExplorer('multiProject');
  const getCatalogAsset = useLinkToExplorer('catalogAsset');

  const isDataPlatform = isWarehouseAssetId(urn);

  const warehouseEntityQuery = useWarehouseEntity({
    accountId,
    filter: {
      urn: urn ?? '',
      parent_urn: null,
      type: null,
    },
    pause: !urn || isDataPlatform,
  });
  const warehouseEntity = warehouseEntityQuery.data;
  const catalogPath = warehouseEntity?.catalogPath;

  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = [
      {
        text: 'Catalog',
        href: getMultiProjectLink({
          accountId: accountId ?? 0,
        }),
      },
    ];

    if (isDataPlatform) {
      return [
        ...baseBreadcrumbs,
        {
          text: urn ?? '',
          href: getCatalogAsset({
            accountId: accountId ?? 0,
            assetId: urn ?? '',
          }),
        },
      ];
    }

    const isTable = warehouseEntity?.type === WarehouseEntityType.Table;
    const isView = warehouseEntity?.type === WarehouseEntityType.View;
    const isSchema = warehouseEntity?.type === WarehouseEntityType.Schema;

    const rest =
      catalogPath?.map(({ urn, name }) => ({
        text: name,
        href: getCatalogAsset({
          accountId: accountId ?? 0,
          assetId: urn,
        }),
      })) ?? [];

    // add tables/views breadcrumb if we're in a schema and the tab is set
    if (isSchema) {
      const categoryBreadcrumb = {
        text: activeTab === 'tables' ? 'Tables' : 'Views',
        href: getCatalogAsset({
          accountId: accountId ?? 0,
          assetId: urn ?? '',
          tab: activeTab as 'tables' | 'views',
        }),
      };

      rest?.push(categoryBreadcrumb);
    }

    // add tables/views breadcrumb for table/view entities
    if (isTable || isView) {
      const categoryBreadcrumb = {
        text: isTable ? 'Tables' : 'Views',
        href: getCatalogAsset({
          accountId: accountId ?? 0,
          assetId: catalogPath?.[catalogPath.length - 2]?.urn ?? '',
          tab: isTable ? 'tables' : 'views',
        }),
      };

      rest?.splice(-1, 0, categoryBreadcrumb);
    }

    return [
      ...baseBreadcrumbs,
      ...(warehouseEntity?.platform
        ? [
            {
              text: warehouseEntity?.platform ?? 'Unknown Data Platform',
              href: getCatalogAsset({
                accountId: accountId ?? 0,
                assetId: warehouseEntity?.platform ?? '',
              }),
            },
          ]
        : []),
      ...rest,
    ];
  }, [
    accountId,
    activeTab,
    catalogPath,
    getCatalogAsset,
    getMultiProjectLink,
    isDataPlatform,
    urn,
    warehouseEntity?.platform,
    warehouseEntity?.type,
  ]);

  return <Breadcrumbs className="mb-3" breadcrumbs={breadcrumbs} />;
};
